/**
 * @fileoverview Confirmation popup for recording an instructor-attested offline
 * consent grant (a signed paper form collected in person) via
 * POST /students/{id}/consent/mark-granted.
 *
 * This is a manual override of the emailed consent flow, so the dialog gates the
 * action behind an explicit attestation checkbox and, for under-13 students,
 * surfaces the COPPA email-plus notice: the parent is emailed a cancel link when
 * an email is on file. On success the caller refreshes the student so the
 * consent badge/recording gate reflect the new "granted" status.
 */

import { useState } from "react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { offlineConsentEmailPlusNotice } from "@/lib/consent";

export function MarkOfflineConsentDialog({ student, open, onOpenChange, onConfirmed }) {
  const [attested, setAttested] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const emailNotice = student ? offlineConsentEmailPlusNotice(student) : null;
  const studentName =
    [student?.first_name, student?.last_name].filter(Boolean).join(" ") || "the student";
  // Same endpoint and the same attestation, but a different act: this family
  // already consented, and is being asked about what changed since. Saying
  // "record offline consent" here would read as though their existing
  // agreement were missing.
  const isUpdate = student?.consent_update_available === true;

  const reset = () => {
    setAttested(false);
    setNote("");
    setSubmitting(false);
  };

  const handleOpenChange = (next) => {
    if (submitting) return; // don't let a click-away abandon an in-flight write
    if (!next) reset();
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!attested || !student) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/students/${student.student_id}/consent/mark-granted`, {
        attestation: true,
        note: note.trim() || undefined,
      });
      const recorded = isUpdate
        ? `Consent for ${studentName} updated to the current terms.`
        : `Consent recorded for ${studentName}.`;
      toast.success(
        res.data?.email_plus_sent ? `${recorded} A confirmation email was sent to the parent.` : recorded
      );
      reset();
      onOpenChange(false);
      onConfirmed?.(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not record consent — try again."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] mx-4 max-w-[calc(100%-2rem)]"
        data-testid="mark-offline-consent-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isUpdate ? "Update consent to current terms" : "Record offline consent"}</DialogTitle>
          <DialogDescription className="text-[var(--text-secondary)]">
            {isUpdate ? (
              <>
                {studentName} consented under an earlier version of our recording terms, which have
                since changed. Use this once you have confirmed the current terms with them
                directly. Their existing consent stays in place either way — recording is not
                interrupted, and nothing is deleted.
              </>
            ) : (
              <>
                Use this only when you have collected a signed consent form on paper or in person,
                instead of the emailed request. It’s recorded in the consent ledger under your name.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <label className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
          <input
            type="checkbox"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
            className="mt-1 h-4 w-4 accent-[var(--state-success)]"
            data-testid="offline-consent-attestation"
          />
          <span>
            {isUpdate
              ? `I have confirmed the current recording terms with ${studentName} or their parent.`
              : `I have obtained and retain a signed consent form for ${studentName}.`}
          </span>
        </label>

        {emailNotice && (
          <p
            className="text-sm text-[var(--state-warning)] bg-[var(--state-warning-dim)] border border-[var(--state-warning-border)] rounded-lg p-3"
            data-testid="offline-consent-email-notice"
          >
            {emailNotice}
          </p>
        )}

        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            isUpdate
              ? "Optional note (e.g. confirmed by phone, 9 Aug)"
              : "Optional note (e.g. where the signed form is filed)"
          }
          className="bg-[var(--studio-interactive)] border-[var(--studio-divider-strong)] text-[var(--text-primary)]"
          data-testid="offline-consent-note"
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!attested || submitting}
            data-testid="offline-consent-confirm"
          >
            {submitting ? "Recording…" : isUpdate ? "Update consent" : "Record consent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
